import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import TrendArticleCard from './components/TrendArticleCard';
import TrendArticleDetailDialog from './components/TrendArticleDetailDialog';
import {
  featureTrendArticle,
  fetchTrendArticles,
  fetchTrendOpportunities,
  fetchTrendStories,
  generateTrendReport,
  hideTrendArticle,
  publishTrendReport,
  updateTrendReport,
} from './service';
import { useTrendsOverview } from './hooks/useTrendsOverview';
import { useTrendsReports } from './hooks/useTrendsReports';
import type { TrendArticle, TrendOpportunity, TrendStory } from './types';
import { HEADING_SX, PAGE_HEADING_SX, SECTION_HEADING_SX, TREND_COLORS } from './trendTheme';

function getArticleQualityLabel(article: TrendArticle): string {
  if (article.qualityStatus === 'requires_review') return 'Granskning';
  if (article.qualityStatus === 'blocked') return 'Blockerad';
  return 'Publicerbar';
}

function isArticlePublishable(article: TrendArticle): boolean {
  return article.qualityStatus === 'publishable';
}

function isStoryPublishable(story: TrendStory): boolean {
  return story.publicationQuality.passes;
}

function isOpportunityPublishable(opportunity: TrendOpportunity): boolean {
  return opportunity.publicationQuality.passes;
}

function getArticleReviewReasons(article: TrendArticle): string[] {
  return Array.from(new Set([
    ...(article.publicationQuality?.reasons || []),
    ...(article.activationQuality?.reasons || []),
  ]));
}

function getReportStatusLabel(status: string): string {
  if (status === 'published') return 'aktiv i trendmodulen';
  if (status === 'review') return 'redo för granskning';
  return 'utkast';
}

export default function TrendsEditorialPage() {
  const { reports, loading: reportsLoading, error: reportsError, reload: reloadReports } = useTrendsReports();
  const { data: overviewData, reload: reloadOverview } = useTrendsOverview();
  const [articles, setArticles] = useState<TrendArticle[]>([]);
  const [stories, setStories] = useState<TrendStory[]>([]);
  const [opportunities, setOpportunities] = useState<TrendOpportunity[]>([]);
  const [articlesLoading, setArticlesLoading] = useState(true);
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  const [selectedArticle, setSelectedArticle] = useState<TrendArticle | null>(null);

  const [pinnedWeeklyIds, setPinnedWeeklyIds] = useState<Set<string>>(new Set());
  const [pinnedTopStoryIds, setPinnedTopStoryIds] = useState<Set<string>>(new Set());
  const [pinnedOpportunityIds, setPinnedOpportunityIds] = useState<Set<string>>(new Set());
  const [hiddenArticleIds, setHiddenArticleIds] = useState<Set<string>>(new Set());
  const [heroArticleId, setHeroArticleId] = useState<string>('');
  const [curationSaving, setCurationSaving] = useState(false);
  const [articleDateFilter, setArticleDateFilter] = useState<string>('all');
  const [qualityFilter, setQualityFilter] = useState<'all' | 'non_publishable' | 'blocked' | 'review'>('all');
  const blockedArticles = articles.filter((article) => article.qualityStatus === 'blocked');
  const reviewArticles = articles.filter((article) => article.qualityStatus === 'requires_review');
  const publishableArticles = articles.filter((article) => isArticlePublishable(article));
  const publishableStories = stories.filter((story) => isStoryPublishable(story));
  const publishableOpportunities = opportunities.filter((opportunity) => isOpportunityPublishable(opportunity));
  const activeOverviewReport = overviewData?.report || null;
  const nonPublishableArticles = articles.filter((article) => article.qualityStatus !== 'publishable');
  const blockedStories = stories.filter((story) => !story.publicationQuality.passes);
  const filteredArticles = qualityFilter === 'blocked'
    ? blockedArticles
    : qualityFilter === 'review'
      ? reviewArticles
      : qualityFilter === 'non_publishable'
        ? nonPublishableArticles
        : articles;
  const filteredStories = qualityFilter === 'all'
    ? stories
    : qualityFilter === 'review'
      ? []
      : blockedStories;
  const showingNonPublishable = qualityFilter !== 'all';

  const loadArticles = useCallback(async () => {
    setArticlesLoading(true);
    try {
      const params: { minDate?: string } = {};
      if (articleDateFilter === '7d') {
        params.minDate = new Date(Date.now() - 7 * 86400000).toISOString();
      } else if (articleDateFilter === '30d') {
        params.minDate = new Date(Date.now() - 30 * 86400000).toISOString();
      } else if (articleDateFilter === '3d') {
        params.minDate = new Date(Date.now() - 3 * 86400000).toISOString();
      }
      const [fetchedArticles, fetchedStories, fetchedOpportunities] = await Promise.all([
        fetchTrendArticles({ ...params, qualityScope: 'all' }),
        fetchTrendStories({ qualityScope: 'all' }),
        fetchTrendOpportunities(),
      ]);
      setArticles(fetchedArticles);
      setStories(fetchedStories);
      setOpportunities(fetchedOpportunities);
    } finally {
      setArticlesLoading(false);
    }
  }, [articleDateFilter]);

  useEffect(() => {
    void loadArticles();
  }, [loadArticles]);

  useEffect(() => {
    if (!activeOverviewReport) {
      setPinnedWeeklyIds(new Set());
      setPinnedTopStoryIds(new Set());
      setPinnedOpportunityIds(new Set());
      setHiddenArticleIds(new Set());
      setHeroArticleId('');
      return;
    }
    setPinnedWeeklyIds(new Set(activeOverviewReport.pinnedWeeklyArticleIds || []));
    setPinnedTopStoryIds(new Set(activeOverviewReport.pinnedTopStoryIds || []));
    setPinnedOpportunityIds(new Set(activeOverviewReport.pinnedOpportunityIds || []));
    setHiddenArticleIds(new Set(activeOverviewReport.hiddenArticleIds || []));
    setHeroArticleId(activeOverviewReport.heroArticleId || '');
  }, [activeOverviewReport]);

  const handleGenerate = async () => {
    await generateTrendReport();
    setActionMessage('Ny trendrapport skapad.');
    await reloadReports();
    await reloadOverview();
    await loadArticles();
  };

  const handlePublish = async (reportId: string) => {
    await publishTrendReport(reportId);
    setActionMessage('Trendrapporten är nu aktiv i trendmodulen.');
    await reloadReports();
    await reloadOverview();
    await loadArticles();
  };

  const handleToggleFeature = async (article: TrendArticle) => {
    await featureTrendArticle(article._id, !article.featured);
    setActionMessage(article.featured ? 'Framlyft borttagen.' : 'Artikel framlyft.');
    await loadArticles();
  };

  const handleHide = async (articleId: string) => {
    await hideTrendArticle(articleId, true);
    setActionMessage('Artikel dold.');
    await loadArticles();
  };

  const togglePinnedWeekly = (articleId: string) => {
    setPinnedWeeklyIds((prev) => {
      const next = new Set(prev);
      if (next.has(articleId)) next.delete(articleId); else next.add(articleId);
      return next;
    });
  };

  const togglePinnedTopStory = (storyId: string) => {
    setPinnedTopStoryIds((prev) => {
      const next = new Set(prev);
      if (next.has(storyId)) next.delete(storyId); else next.add(storyId);
      return next;
    });
  };

  const toggleHiddenArticle = (articleId: string) => {
    setHiddenArticleIds((prev) => {
      const next = new Set(prev);
      if (next.has(articleId)) next.delete(articleId); else next.add(articleId);
      return next;
    });
  };

  const togglePinnedOpportunity = (opportunityId: string) => {
    setPinnedOpportunityIds((prev) => {
      const next = new Set(prev);
      if (next.has(opportunityId)) next.delete(opportunityId); else next.add(opportunityId);
      return next;
    });
  };

  const handleSaveCuration = async () => {
    if (!activeOverviewReport?._id) {
      setActionMessage('Ingen live-rapport hittad för overviewn. Publicera eller generera en rapport först.');
      return;
    }
    const publishableArticleIds = new Set(publishableArticles.map((article) => article._id));
    const publishableStoryIds = new Set(publishableStories.map((story) => story._id));
    const publishableOpportunityIds = new Set(publishableOpportunities.map((opportunity) => opportunity._id));
    const sanitizedHeroArticleId = heroArticleId && publishableArticleIds.has(heroArticleId)
      ? heroArticleId
      : '';
    const sanitizedPinnedWeeklyIds = Array.from(pinnedWeeklyIds).filter((articleId) => publishableArticleIds.has(articleId));
    const sanitizedPinnedTopStoryIds = Array.from(pinnedTopStoryIds).filter((storyId) => publishableStoryIds.has(storyId));
    const sanitizedPinnedOpportunityIds = Array.from(pinnedOpportunityIds).filter((opportunityId) => publishableOpportunityIds.has(opportunityId));
    const droppedArticleSelections = sanitizedPinnedWeeklyIds.length !== pinnedWeeklyIds.size
      || (heroArticleId && sanitizedHeroArticleId !== heroArticleId);
    const droppedStorySelections = sanitizedPinnedTopStoryIds.length !== pinnedTopStoryIds.size;
    const droppedOpportunitySelections = sanitizedPinnedOpportunityIds.length !== pinnedOpportunityIds.size;
    setCurationSaving(true);
    try {
      await updateTrendReport(activeOverviewReport._id, {
        heroArticleId: sanitizedHeroArticleId || null,
        pinnedWeeklyArticleIds: sanitizedPinnedWeeklyIds,
        pinnedTopStoryIds: sanitizedPinnedTopStoryIds,
        pinnedOpportunityIds: sanitizedPinnedOpportunityIds,
        hiddenArticleIds: Array.from(hiddenArticleIds),
      } as any);
      setHeroArticleId(sanitizedHeroArticleId);
      setPinnedWeeklyIds(new Set(sanitizedPinnedWeeklyIds));
      setPinnedTopStoryIds(new Set(sanitizedPinnedTopStoryIds));
      setPinnedOpportunityIds(new Set(sanitizedPinnedOpportunityIds));
      setActionMessage(
        droppedArticleSelections || droppedStorySelections || droppedOpportunitySelections
          ? 'Framsidans styrning sparad. Val som ännu inte är publicerbara togs bort automatiskt.'
          : 'Framsidans styrning sparad.',
      );
      await reloadReports();
      await reloadOverview();
    } catch {
      setActionMessage('Kunde inte spara styrningen.');
    } finally {
      setCurationSaving(false);
    }
  };

  const handleArticleUpdated = (updatedArticle: TrendArticle) => {
    setArticles((previous) => previous.map((article) => (
      article._id === updatedArticle._id ? updatedArticle : article
    )));
    setSelectedArticle(updatedArticle);
    setActionMessage('Artikel uppdaterad.');
  };

  return (
    <Box sx={{ bgcolor: TREND_COLORS.pageBg, minHeight: '100vh', mx: -3, mt: -3, px: { xs: 2, md: 4 }, py: { xs: 3, md: 5 } }}>
      <Stack spacing={4} sx={{ maxWidth: 1200, mx: 'auto' }}>
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2}>
          <Typography sx={PAGE_HEADING_SX}>REDAKTIONELLT</Typography>
          <Button
            variant="contained"
            onClick={handleGenerate}
            sx={{
              ...HEADING_SX,
              fontSize: '0.8rem',
              bgcolor: TREND_COLORS.navy,
              px: 3,
              '&:hover': { bgcolor: TREND_COLORS.navyLight },
            }}
          >
            Generera rapportutkast
          </Button>
        </Stack>

        {actionMessage && <Alert severity="success" onClose={() => setActionMessage(null)}>{actionMessage}</Alert>}
        {reportsError && <Alert severity="error">{reportsError}</Alert>}
        <Alert severity="info">
          Redaktionellt är arbetsbänken efter en full trendkörning. Börja med artiklar som kräver review, kontrollera blockerade objekt, granska rapportutkastet och gör rapporten aktiv först när materialet är redo för översikten.
        </Alert>
        {(nonPublishableArticles.length > 0 || blockedStories.length > 0) && (
          <Alert severity="warning">
            Kvalitetskontraktet stoppar just nu {blockedArticles.length} blockerade artiklar, {reviewArticles.length} artiklar som kräver granskning
            och {blockedStories.length} blockerade berättelser från publika ytor. Exakta orsaker visas i artikeldialogen och direkt i berättelselistan nedan.
          </Alert>
        )}

        {reviewArticles.length > 0 && (
          <Box sx={{ bgcolor: TREND_COLORS.cardBg, borderRadius: 3, p: 3, border: `1px solid ${TREND_COLORS.divider}` }}>
            <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2} sx={{ mb: 2.5 }}>
              <Box>
                <Typography sx={SECTION_HEADING_SX}>ARTIKLAR SOM KRÄVER REVIEW</Typography>
                <Typography variant="body2" sx={{ color: TREND_COLORS.textSecondary, mt: 0.75 }}>
                  Dessa artiklar syns i redaktionellt läge men blockeras från översikten tills de blivit publicerbara.
                </Typography>
              </Box>
              <Chip
                label={`${reviewArticles.length} i kö`}
                sx={{ alignSelf: { xs: 'flex-start', md: 'center' }, bgcolor: '#FFF4D6', color: '#8A5D00', fontWeight: 700 }}
              />
            </Stack>
            <Stack spacing={1.5}>
              {reviewArticles.map((article) => (
                <Box
                  key={article._id}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: '#FFF9E8',
                    border: '1px solid #F0D18A',
                  }}
                >
                  <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2}>
                    <Box sx={{ minWidth: 0 }}>
                      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                        <Chip label="Kräver granskning" size="small" sx={{ bgcolor: '#FFF4D6', color: '#8A5D00', fontWeight: 700 }} />
                        <Chip label={(article.category || 'okänd').toUpperCase()} size="small" variant="outlined" />
                      </Stack>
                      <Typography variant="subtitle2" sx={{ color: TREND_COLORS.navy, fontWeight: 700 }}>
                        {article.title}
                      </Typography>
                      <Stack component="ul" spacing={0.25} sx={{ m: 0, mt: 1, pl: 2 }}>
                        {getArticleReviewReasons(article).slice(0, 3).map((reason) => (
                          <Typography component="li" key={reason} variant="body2" sx={{ color: TREND_COLORS.textSecondary, lineHeight: 1.6 }}>
                            {reason}
                          </Typography>
                        ))}
                      </Stack>
                    </Box>
                    <Button
                      variant="contained"
                      onClick={() => setSelectedArticle(article)}
                      sx={{
                        ...HEADING_SX,
                        fontSize: '0.72rem',
                        bgcolor: TREND_COLORS.navy,
                        alignSelf: { xs: 'flex-start', md: 'center' },
                        '&:hover': { bgcolor: TREND_COLORS.navyLight },
                      }}
                    >
                      Öppna för granskning
                    </Button>
                  </Stack>
                </Box>
              ))}
            </Stack>
          </Box>
        )}

        <Box sx={{ bgcolor: TREND_COLORS.cardBg, borderRadius: 3, p: 3, border: `1px solid ${TREND_COLORS.divider}` }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', md: 'center' }}
            spacing={2}
          >
            <Box>
              <Typography sx={SECTION_HEADING_SX}>KVALITETSFILTER</Typography>
              <Typography variant="body2" sx={{ color: TREND_COLORS.textSecondary, mt: 0.75 }}>
                Växla mellan blockerat, granskning och allt för att se exakt vad som stoppas av kvalitetskontraktet.
              </Typography>
            </Box>
            <TextField
              select
              size="small"
              value={qualityFilter}
              onChange={(event) => setQualityFilter(event.target.value as 'all' | 'non_publishable' | 'blocked' | 'review')}
              sx={{ minWidth: 240, '& .MuiInputBase-root': { fontSize: '0.8rem' } }}
            >
              <MenuItem value="all">Alla objekt</MenuItem>
              <MenuItem value="non_publishable">Allt som inte kan publiceras</MenuItem>
              <MenuItem value="blocked">Endast blockerade objekt</MenuItem>
              <MenuItem value="review">Endast kräver granskning</MenuItem>
            </TextField>
          </Stack>
          {showingNonPublishable && (
            <Typography variant="body2" sx={{ color: TREND_COLORS.textMuted, mt: 2 }}>
              {qualityFilter === 'review'
                ? `Visar ${reviewArticles.length} artiklar som kräver granskning.`
                : qualityFilter === 'blocked'
                  ? `Visar ${blockedArticles.length} blockerade artiklar och ${blockedStories.length} blockerade berättelser.`
                  : `Visar ${nonPublishableArticles.length} artiklar och ${blockedStories.length} berättelser som inte kan publiceras just nu.`}
            </Typography>
          )}
        </Box>

        <Box sx={{ bgcolor: TREND_COLORS.cardBg, borderRadius: 3, p: 3, border: `1px solid ${TREND_COLORS.divider}` }}>
          <Typography sx={{ ...SECTION_HEADING_SX, mb: 1.25 }}>RAPPORTER</Typography>
          <Typography variant="body2" sx={{ color: TREND_COLORS.textSecondary, mb: 2.5, maxWidth: 760 }}>
            Varje rapport här är en intern veckosammanställning för trendmodulen. När du gör en rapport aktiv används den som den live-rapport
            som översikten i trendmodulen utgår från. Den publiceras inte till externa kanaler.
          </Typography>
          {reportsLoading ? (
            <CircularProgress sx={{ color: TREND_COLORS.navy }} />
          ) : (
            <Stack spacing={1.5}>
              {reports.map((report) => (
                <Stack
                  key={report._id}
                  direction={{ xs: 'column', md: 'row' }}
                  justifyContent="space-between"
                  alignItems={{ xs: 'flex-start', md: 'center' }}
                  spacing={2}
                  sx={{ py: 1.5, borderBottom: `1px solid ${TREND_COLORS.divider}` }}
                >
                  <div>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: TREND_COLORS.navy }}>
                      {report.title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: TREND_COLORS.textMuted }}>
                      {report.issueDate?.slice(0, 10)} · {getReportStatusLabel(report.status)}
                    </Typography>
                  </div>
                  {report.status !== 'published' && (
                    <Button variant="outlined" onClick={() => handlePublish(report._id)} sx={{ borderColor: TREND_COLORS.navy, color: TREND_COLORS.navy }}>
                      Gör aktiv i trendmodulen
                    </Button>
                  )}
                </Stack>
              ))}
            </Stack>
          )}
        </Box>

        <Box sx={{ bgcolor: TREND_COLORS.cardBg, borderRadius: 3, p: 3, border: `1px solid ${TREND_COLORS.divider}` }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2.5 }}>
            <Typography sx={SECTION_HEADING_SX}>FRAMSIDESSTYRNING</Typography>
            <Button
              variant="contained"
              disabled={curationSaving}
              onClick={handleSaveCuration}
              sx={{
                ...HEADING_SX,
                fontSize: '0.75rem',
                bgcolor: TREND_COLORS.olive,
                '&:hover': { bgcolor: '#4a6741' },
              }}
            >
              {curationSaving ? 'Sparar...' : 'Spara framsida'}
            </Button>
          </Stack>

          <Typography variant="body2" sx={{ color: TREND_COLORS.textSecondary, mb: 3 }}>
            Välj vilka artiklar som ska visas i "Det här präglar veckan", vilka berättelser som ska pinnas i "Toppberättelser"
            , vilka möjligheter som ska visas i overviewns möjlighetspanel och vilka artiklar som ska döljas. Sparar till den senaste rapporten.
          </Typography>
          <Alert severity="info" sx={{ mb: 3 }}>
            Endast publicerbara artiklar, berättelser och möjligheter kan visas på översikten. Om inga möjligheter väljs manuellt används fortfarande det automatiska urvalet.
          </Alert>

          <Stack spacing={3}>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: TREND_COLORS.navy, mb: 1 }}>
                Hero-artikel
              </Typography>
              <select
                value={heroArticleId}
                onChange={(event) => setHeroArticleId(event.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  borderRadius: 6,
                  border: `1px solid ${TREND_COLORS.divider}`,
                  fontFamily: 'inherit',
                  fontSize: '0.85rem',
                  background: '#fff',
                }}
              >
                <option value="">— Automatiskt urval —</option>
                {articles.map((a) => (
                  <option key={a._id} value={a._id} disabled={!isArticlePublishable(a)}>
                    {a.title?.slice(0, 80) || a._id} ({(a.category || '').toUpperCase()}{a.provenance === 'discovery' ? ' · DISCOVERY' : ''}{a.qualityStatus === 'publishable' ? '' : ` · ${getArticleQualityLabel(a).toUpperCase()}`})
                  </option>
                ))}
              </select>
              <Typography variant="caption" sx={{ display: 'block', mt: 0.75, color: TREND_COLORS.textMuted }}>
                Artiklar som kräver granskning eller är blockerade visas i listan men kan inte väljas som huvudartikel förrän de blivit publicerbara.
              </Typography>
            </Box>

            <Divider sx={{ borderColor: TREND_COLORS.divider }} />

            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: TREND_COLORS.navy, mb: 1.5 }}>
                Pinna till "Det här präglar veckan" ({pinnedWeeklyIds.size} valda)
              </Typography>
              <Stack spacing={0.75} sx={{ maxHeight: 320, overflowY: 'auto' }}>
                {filteredArticles.length === 0 && (
                  <Typography variant="body2" sx={{ color: TREND_COLORS.textMuted, px: 1, py: 1 }}>
                    {showingNonPublishable ? 'Inga artiklar för det valda kvalitetsfiltret just nu.' : 'Inga artiklar att visa.'}
                  </Typography>
                )}
                {filteredArticles.map((article) => (
                  <Stack
                    key={article._id}
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                      bgcolor: pinnedWeeklyIds.has(article._id) ? TREND_COLORS.goldLight : 'transparent',
                      cursor: isArticlePublishable(article) ? 'pointer' : 'not-allowed',
                      opacity: isArticlePublishable(article) ? 1 : 0.7,
                      '&:hover': { bgcolor: TREND_COLORS.softNavy },
                    }}
                    onClick={() => {
                      if (!isArticlePublishable(article)) return;
                      togglePinnedWeekly(article._id);
                    }}
                  >
                    <Checkbox
                      size="small"
                      checked={pinnedWeeklyIds.has(article._id)}
                      disabled={!isArticlePublishable(article)}
                      sx={{ p: 0 }}
                    />
                    <Stack spacing={0.25} sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" noWrap sx={{ color: TREND_COLORS.textPrimary }}>
                        {article.title}
                      </Typography>
                      {!isArticlePublishable(article) && (
                        <Typography variant="caption" noWrap sx={{ color: '#8A5D00' }}>
                          Kan inte visas på översikten ännu
                        </Typography>
                      )}
                    </Stack>
                    <Stack direction="row" spacing={0.5}>
                      {article.qualityStatus !== 'publishable' && (
                        <Chip label={getArticleQualityLabel(article)} size="small" sx={{ fontSize: '0.55rem', height: 18, bgcolor: '#fff4d6', color: '#8a5d00', fontWeight: 700, minWidth: 0 }} />
                      )}
                      {article.provenance === 'discovery' && (
                        <Chip label="D" size="small" sx={{ fontSize: '0.55rem', height: 18, bgcolor: TREND_COLORS.terracotta, color: '#fff', fontWeight: 700, minWidth: 0 }} />
                      )}
                      <Chip label={article.category} size="small" sx={{ fontSize: '0.6rem', height: 20 }} />
                    </Stack>
                  </Stack>
                ))}
              </Stack>
            </Box>

            <Divider sx={{ borderColor: TREND_COLORS.divider }} />

            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: TREND_COLORS.navy, mb: 1.5 }}>
                Pinna till "Toppberättelser" ({pinnedTopStoryIds.size} valda)
              </Typography>
              <Stack spacing={0.75} sx={{ maxHeight: 260, overflowY: 'auto' }}>
                {filteredStories.length === 0 && (
                  <Typography variant="body2" sx={{ color: TREND_COLORS.textMuted, px: 1, py: 1 }}>
                    {showingNonPublishable ? 'Inga berättelser för det valda kvalitetsfiltret just nu.' : 'Inga berättelser att visa.'}
                  </Typography>
                )}
                {filteredStories.map((story) => (
                  <Stack
                    key={story._id}
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                      bgcolor: pinnedTopStoryIds.has(story._id) ? TREND_COLORS.goldLight : 'transparent',
                      cursor: isStoryPublishable(story) ? 'pointer' : 'not-allowed',
                      opacity: isStoryPublishable(story) ? 1 : 0.7,
                      '&:hover': { bgcolor: TREND_COLORS.softNavy },
                    }}
                    onClick={() => {
                      if (!isStoryPublishable(story)) return;
                      togglePinnedTopStory(story._id);
                    }}
                  >
                    <Checkbox
                      size="small"
                      checked={pinnedTopStoryIds.has(story._id)}
                      disabled={!isStoryPublishable(story)}
                      sx={{ p: 0 }}
                    />
                    <Stack spacing={0.25} sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" noWrap sx={{ color: TREND_COLORS.textPrimary }}>
                        {story.title}
                      </Typography>
                      {!story.publicationQuality.passes && (
                        <Typography variant="caption" noWrap sx={{ color: '#8a5d00' }}>
                          {story.publicationQuality.reasons.join(' ')}
                        </Typography>
                      )}
                    </Stack>
                    <Stack direction="row" spacing={0.5}>
                      {!story.publicationQuality.passes && (
                        <Chip label="Blockerad" size="small" sx={{ fontSize: '0.55rem', height: 18, bgcolor: '#fff4d6', color: '#8a5d00', fontWeight: 700 }} />
                      )}
                      <Chip label={story.category} size="small" sx={{ fontSize: '0.6rem', height: 20 }} />
                      {story.provenance && story.provenance !== 'feed' && (
                        <Chip label="D" size="small" sx={{ fontSize: '0.55rem', height: 18, minWidth: 18, bgcolor: TREND_COLORS.navy, color: '#fff', fontWeight: 700 }} />
                      )}
                    </Stack>
                  </Stack>
                ))}
              </Stack>
            </Box>

            <Divider sx={{ borderColor: TREND_COLORS.divider }} />

            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: TREND_COLORS.navy, mb: 1.5 }}>
                Visa i "Möjligheter" på översikten ({pinnedOpportunityIds.size} valda)
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', mb: 1.5, color: TREND_COLORS.textMuted }}>
                Lämna tomt för automatiskt urval. Om du väljer en eller flera möjligheter visas just de på overviewn.
              </Typography>
              <Stack spacing={0.75} sx={{ maxHeight: 260, overflowY: 'auto' }}>
                {opportunities.length === 0 && (
                  <Typography variant="body2" sx={{ color: TREND_COLORS.textMuted, px: 1, py: 1 }}>
                    Inga möjligheter att visa ännu.
                  </Typography>
                )}
                {opportunities.map((opportunity) => (
                  <Stack
                    key={opportunity._id}
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                      bgcolor: pinnedOpportunityIds.has(opportunity._id) ? TREND_COLORS.goldLight : 'transparent',
                      cursor: isOpportunityPublishable(opportunity) ? 'pointer' : 'not-allowed',
                      opacity: isOpportunityPublishable(opportunity) ? 1 : 0.7,
                      '&:hover': { bgcolor: TREND_COLORS.softNavy },
                    }}
                    onClick={() => {
                      if (!isOpportunityPublishable(opportunity)) return;
                      togglePinnedOpportunity(opportunity._id);
                    }}
                  >
                    <Checkbox
                      size="small"
                      checked={pinnedOpportunityIds.has(opportunity._id)}
                      disabled={!isOpportunityPublishable(opportunity)}
                      sx={{ p: 0 }}
                    />
                    <Stack spacing={0.25} sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" noWrap sx={{ color: TREND_COLORS.textPrimary }}>
                        {opportunity.title}
                      </Typography>
                      {!isOpportunityPublishable(opportunity) ? (
                        <Typography variant="caption" noWrap sx={{ color: '#8a5d00' }}>
                          {opportunity.publicationQuality.reasons[0] || 'Kan inte visas på översikten ännu'}
                        </Typography>
                      ) : (
                        <Typography variant="caption" noWrap sx={{ color: TREND_COLORS.textMuted }}>
                          {opportunity.summary}
                        </Typography>
                      )}
                    </Stack>
                    <Stack direction="row" spacing={0.5}>
                      {!isOpportunityPublishable(opportunity) && (
                        <Chip label="Blockerad" size="small" sx={{ fontSize: '0.55rem', height: 18, bgcolor: '#fff4d6', color: '#8a5d00', fontWeight: 700 }} />
                      )}
                      <Chip label={opportunity.type} size="small" sx={{ fontSize: '0.55rem', height: 18 }} />
                    </Stack>
                  </Stack>
                ))}
              </Stack>
            </Box>

            <Divider sx={{ borderColor: TREND_COLORS.divider }} />

            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: TREND_COLORS.navy, mb: 1.5 }}>
                Dölj från översikt ({hiddenArticleIds.size} dolda)
              </Typography>
              <Stack spacing={0.75} sx={{ maxHeight: 260, overflowY: 'auto' }}>
                {filteredArticles.length === 0 && (
                  <Typography variant="body2" sx={{ color: TREND_COLORS.textMuted, px: 1, py: 1 }}>
                    {showingNonPublishable ? 'Inga artiklar för det valda kvalitetsfiltret just nu.' : 'Inga artiklar att visa.'}
                  </Typography>
                )}
                {filteredArticles.map((article) => (
                  <Stack
                    key={article._id}
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                      bgcolor: hiddenArticleIds.has(article._id) ? '#fde8e8' : 'transparent',
                      cursor: 'pointer',
                      '&:hover': { bgcolor: TREND_COLORS.softNavy },
                    }}
                    onClick={() => toggleHiddenArticle(article._id)}
                  >
                    <Checkbox
                      size="small"
                      checked={hiddenArticleIds.has(article._id)}
                      sx={{ p: 0 }}
                    />
                    <Typography
                      variant="body2"
                      noWrap
                      sx={{
                        flex: 1,
                        color: hiddenArticleIds.has(article._id) ? TREND_COLORS.textMuted : TREND_COLORS.textPrimary,
                        textDecoration: hiddenArticleIds.has(article._id) ? 'line-through' : 'none',
                      }}
                    >
                      {article.title}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Box>
          </Stack>
        </Box>

        <Stack spacing={2.5}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography sx={SECTION_HEADING_SX}>
              ARTIKLAR ({filteredArticles.length}{showingNonPublishable ? ` av ${articles.length}` : ''})
            </Typography>
            <TextField
              select
              size="small"
              value={articleDateFilter}
              onChange={(e) => setArticleDateFilter(e.target.value)}
              sx={{ minWidth: 200, '& .MuiInputBase-root': { fontSize: '0.8rem' } }}
            >
              <MenuItem value="all">Alla artiklar</MenuItem>
              <MenuItem value="3d">Senaste 3 dagarna</MenuItem>
              <MenuItem value="7d">Senaste 7 dagarna</MenuItem>
              <MenuItem value="30d">Senaste 30 dagarna</MenuItem>
            </TextField>
          </Stack>
          {articlesLoading ? (
            <CircularProgress sx={{ color: TREND_COLORS.navy }} />
          ) : filteredArticles.length === 0 ? (
            <Alert severity="info">
              {showingNonPublishable
                ? 'Inga artiklar i valt kvalitetsläge matchar det aktuella datumfiltret.'
                : 'Det finns inga artiklar att visa för det aktuella datumfiltret.'}
            </Alert>
          ) : (
            <Grid container spacing={3}>
              {filteredArticles.map((article) => (
                <Grid key={article._id} size={{ xs: 12, sm: 6, lg: 4 }}>
                  <Stack spacing={1}>
                    <TrendArticleCard article={article} onReadMore={(entry) => setSelectedArticle(entry)} />
                    <Stack direction="row" spacing={1}>
                      {article.qualityStatus === 'requires_review' && (
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => setSelectedArticle(article)}
                          sx={{
                            fontSize: '0.7rem',
                            ...HEADING_SX,
                            bgcolor: TREND_COLORS.navy,
                            '&:hover': { bgcolor: TREND_COLORS.navyLight },
                          }}
                        >
                          Gör granskning
                        </Button>
                      )}
                      <Button
                        size="small"
                        variant={article.featured ? 'contained' : 'outlined'}
                        onClick={() => handleToggleFeature(article)}
                        sx={{
                          fontSize: '0.7rem',
                          ...HEADING_SX,
                          ...(article.featured
                            ? { bgcolor: TREND_COLORS.gold, color: TREND_COLORS.navy, '&:hover': { bgcolor: '#c9a94e' } }
                            : { borderColor: TREND_COLORS.gold, color: TREND_COLORS.terracotta }),
                        }}
                      >
                        {article.featured ? '★ Framlyft' : 'Lyft fram'}
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        color="warning"
                        onClick={() => handleHide(article._id)}
                        sx={{ fontSize: '0.7rem', ...HEADING_SX }}
                      >
                        Dölj
                      </Button>
                      {article.createdAt && (
                        <Typography variant="caption" sx={{ color: TREND_COLORS.textMuted, ml: 'auto', alignSelf: 'center' }}>
                          {new Date(article.createdAt).toLocaleDateString('sv-SE', { day: 'numeric', month: 'short' })}
                        </Typography>
                      )}
                    </Stack>
                    {article.qualityStatus !== 'publishable' && (
                      <Alert severity="warning" sx={{ py: 0.75 }}>
                        <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, mb: 0.5 }}>
                          {article.qualityStatus === 'requires_review'
                            ? 'Kräver granskning innan vidare användning'
                            : 'Blockerad från publika ytor'}
                        </Typography>
                        <Stack component="ul" spacing={0.25} sx={{ m: 0, pl: 2 }}>
                          {getArticleReviewReasons(article).map((reason) => (
                            <Typography component="li" key={reason} variant="caption">
                              {reason}
                            </Typography>
                          ))}
                        </Stack>
                      </Alert>
                    )}
                  </Stack>
                </Grid>
              ))}
            </Grid>
          )}
        </Stack>

        <TrendArticleDetailDialog
          articleId={selectedArticle?._id || null}
          open={!!selectedArticle}
          onClose={() => setSelectedArticle(null)}
          editable
          qualityScope="all"
          onUpdated={handleArticleUpdated}
        />
      </Stack>
    </Box>
  );
}
